import { z } from 'zod';
import { ETAPES } from './etapes.js';

export const etapeSchema = z.enum(ETAPES);

export const REGIMES_TAGS = ['Végétarien', 'Végane', 'Sans gluten', 'Sans porc', 'Sans lactose', 'Allergie'] as const;

const nomPersonne = z.string().trim().min(2, 'Indiquez un prénom et un nom.').max(80);
const texteCourt = z.string().trim().max(200);
/** E-mail facultatif : chaîne vide = absent. */
const emailFacultatif = z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), z.string().trim().toLowerCase().email('Adresse e-mail invalide.').max(120).optional());

/** Un foyer compte de 1 à 3 personnes (couple, couple avec un enfant adulte, colocation…) ; il se déplace d'un bloc. */
export const TAILLE_FOYER_MAX = 3;
export const nombreFoyerSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type NombreFoyer = z.infer<typeof nombreFoyerSchema>;

/** Formulaire d'inscription public (écran 2), partagé front / API. */
export const inscriptionSchema = z
  .object({
    /** Nombre de personnes du foyer (1 à 3). */
    nombre: nombreFoyerSchema.default(1),
    personne1: nomPersonne,
    personne2: z.string().trim().max(80).optional(),
    personne3: z.string().trim().max(80).optional(),
    /** Un e-mail par personne, facultatif ; au moins un dans le foyer. Chaque e-mail reçoit le parcours. */
    email1: emailFacultatif,
    email2: emailFacultatif,
    email3: emailFacultatif,
    telephone: z.string().trim().max(30).optional(),
    /** Demandée seulement si le foyer accepte de recevoir (les convives doivent pouvoir venir). */
    adresse: z.string().trim().max(200).optional(),
    complementAdresse: texteCourt.optional(),
    regimes: z.array(z.enum(REGIMES_TAGS)).default([]),
    precisionsRegime: texteCourt.optional(),
    veutRecevoir: z.boolean(),
    disponibilites: z.array(etapeSchema).default([]),
    /** Personnes au maximum à sa table, hôtes compris (demandé si le foyer reçoit). */
    capaciteMax: z.number().int().min(2).max(20).optional(),
    /** Usage privé : plus de case de consentement, le champ reste accepté pour compatibilité. */
    consentement: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.nombre >= 2 && (!v.personne2 || v.personne2.length < 2)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['personne2'], message: 'Indiquez le prénom et le nom de la 2e personne.' });
    }
    if (v.nombre >= 3 && (!v.personne3 || v.personne3.length < 2)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['personne3'], message: 'Indiquez le prénom et le nom de la 3e personne.' });
    }
    if (emailsInscription(v).every((e) => !e)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email1'], message: 'Indiquez au moins une adresse e-mail dans la voiture : vous y recevrez votre parcours.' });
    }
    if (v.veutRecevoir && v.disponibilites.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['disponibilites'], message: 'Cochez au moins une étape que vous pourriez recevoir.' });
    }
    if (v.veutRecevoir && (!v.adresse || v.adresse.length < 5)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['adresse'], message: 'Indiquez votre adresse : vos convives doivent pouvoir venir chez vous.' });
    }
    if (v.veutRecevoir && v.capaciteMax === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['capaciteMax'], message: 'Indiquez combien de personnes vous pouvez recevoir au maximum.' });
    }
  });
export type Inscription = z.infer<typeof inscriptionSchema>;
export type InscriptionInput = z.input<typeof inscriptionSchema>;

/** E-mails d'une inscription, alignés sur les membres (null = non renseigné). */
export function emailsInscription(v: { nombre: number; email1?: string | undefined; email2?: string | undefined; email3?: string | undefined }): (string | null)[] {
  return [v.email1, v.email2, v.email3].slice(0, Math.max(1, Math.min(3, v.nombre))).map((e) => (e && e.trim() ? e.trim().toLowerCase() : null));
}

/** Destinataires d'un foyer : e-mails distincts des membres présents, plus l'e-mail de contact du foyer. */
export function destinatairesDe(f: { email?: string | null | undefined; membres: { participe: boolean; email?: string | null | undefined }[] }): string[] {
  const vus = new Set<string>();
  const out: string[] = [];
  const ajouter = (e: string | null | undefined) => {
    const n = e?.trim().toLowerCase();
    if (n && !vus.has(n)) {
      vus.add(n);
      out.push(n);
    }
  };
  for (const m of f.membres) if (m.participe) ajouter(m.email);
  ajouter(f.email);
  return out;
}

/** Membres d'une inscription dans l'ordre, selon le nombre déclaré. */
export function membresInscription(v: Pick<Inscription, 'nombre' | 'personne1' | 'personne2' | 'personne3'>): string[] {
  return [v.personne1, v.nombre >= 2 ? v.personne2 : undefined, v.nombre >= 3 ? v.personne3 : undefined].filter((x): x is string => !!x && x.trim().length > 0).map((x) => x.trim());
}

/** « Claire et Julien », « Claire, Julien et Léa » : liste en français. */
export function listeEt(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

/** Nom de famille d'un « Prénom Nom » (tout sauf le premier mot ; le nom entier à défaut). */
export function nomFamille(prenomNom: string): string {
  return prenomNom.trim().split(/\s+/).slice(1).join(' ') || prenomNom.trim();
}

/** Nom de foyer déduit des membres : « Claire Martin », « les Martin », « les Nguyen-Roux ». */
export function nomDeFoyer(membres: string[]): string {
  if (membres.length <= 1) return membres[0] ?? '';
  const noms: string[] = [];
  for (const m of membres) {
    const n = nomFamille(m);
    if (!noms.some((x) => x.toLowerCase() === n.toLowerCase())) noms.push(n);
  }
  return `les ${noms.join('-')}`;
}

/** « Claire et Julien Martin », « Claire, Julien et Léa Martin », « Linh Nguyen et Thomas Roux », « Grace Okafor ». */
export function nomCompletDe(membres: string[], repli = ''): string {
  if (membres.length === 0) return repli;
  if (membres.length === 1) return membres[0]!;
  const nom = nomFamille(membres[0]!);
  const memeNom = nom.length > 0 && membres.every((m) => nomFamille(m).toLowerCase() === nom.toLowerCase());
  const parts = memeNom ? membres.map((m) => m.trim().split(/\s+/)[0] ?? m) : membres;
  const liste = listeEt(parts);
  return memeNom ? `${liste} ${nom}` : liste;
}

const heure = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM');
const jour = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');

/** Création / modification d'un événement (écran 1, non dessiné). */
export const evenementSchema = z.object({
  nom: z.string().trim().min(3).max(120),
  organisation: z.string().trim().max(120).optional(),
  description: z.string().trim().max(1000).optional(),
  date: jour,
  zone: z.string().trim().max(120).optional(),
  horaireEntree: heure,
  horairePlat: heure,
  horaireDessert: heure,
  dateLimiteInscription: jour,
  capaciteMax: z.number().int().positive().max(500).optional(),
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9-]{3,60}$/, 'Lettres minuscules, chiffres et tirets.'),
  revealAdresseMinAvant: z.number().int().min(0).max(1440).nullable().optional(),
  /** Plage de convives par table, hôtes compris. */
  tailleMin: z.number().int().min(2).max(20).default(3),
  tailleMax: z.number().int().min(2).max(20).default(5),
  contactNom: z.string().trim().max(80).optional(),
  contactTelephone: z.string().trim().max(30).optional(),
  /** Étape commune facultative en fin de soirée : tout le monde au même endroit, adresse dévoilée comme les autres. */
  communeActive: z.boolean().default(false),
  communeNom: z.string().trim().max(60).optional(),
  communeHeure: heure.optional(),
  communeLieu: z.string().trim().max(120).optional(),
  communeAdresse: z.string().trim().max(200).optional(),
  communeComplement: z.string().trim().max(120).optional(),
});
export type EvenementInput = z.infer<typeof evenementSchema>;

/** Libellé par défaut de l'étape commune. */
export const COMMUNE_NOM_DEFAUT = 'Fin de soirée';

/** Champs obligatoires de l'étape commune quand elle est activée (vérifiés en plus du schéma de base). */
export function communeValide(v: Pick<EvenementInput, 'communeActive' | 'communeHeure' | 'communeLieu' | 'communeAdresse' | 'horaireDessert'>): { path: 'communeHeure' | 'communeLieu' | 'communeAdresse'; message: string }[] {
  if (!v.communeActive) return [];
  const erreurs: { path: 'communeHeure' | 'communeLieu' | 'communeAdresse'; message: string }[] = [];
  if (!v.communeHeure) erreurs.push({ path: 'communeHeure', message: "Indiquez l'heure de l'étape commune." });
  else if (v.communeHeure <= v.horaireDessert) erreurs.push({ path: 'communeHeure', message: "L'étape commune doit commencer après le dessert." });
  if (!v.communeLieu) erreurs.push({ path: 'communeLieu', message: 'Indiquez le lieu (« Chez Vincent », « Salle des fêtes »…).' });
  if (!v.communeAdresse) erreurs.push({ path: 'communeAdresse', message: "Indiquez l'adresse du lieu." });
  return erreurs;
}

/** Cohérence min ≤ max (vérifiée en plus du schéma de base). */
export function plageValide(v: { tailleMin: number; tailleMax: number }): string | null {
  return v.tailleMin > v.tailleMax ? 'Le minimum de convives par table doit être inférieur ou égal au maximum.' : null;
}

export const affectationHoteSchema = z.object({
  foyerId: z.string().uuid(),
  etape: etapeSchema,
  accordHorsDispo: z.boolean().optional(),
  accordDeuxEtapes: z.boolean().optional(),
});
export const affectationsHotesSchema = z.array(affectationHoteSchema);

export const groupeSchema = z.object({
  etape: etapeSchema,
  hoteFoyerId: z.string().uuid(),
  foyerIds: z.array(z.string().uuid()),
});
export const groupesSchema = z.array(groupeSchema);

export const foyerManuelSchema = z.object({
  nom: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().optional(),
  telephone: z.string().trim().max(30).optional(),
  adresse: z.string().trim().max(200).optional(),
  complementAdresse: texteCourt.optional(),
  veutRecevoir: z.boolean().default(false),
  disponibilites: z.array(etapeSchema).default([]),
  capaciteMax: z.number().int().min(2).max(20).optional(),
  membres: z.array(z.object({ prenomNom: nomPersonne, participe: z.boolean().default(true), regime: texteCourt.optional(), email: emailFacultatif })).min(1).max(TAILLE_FOYER_MAX),
  /** À l'ajout : envoyer l'e-mail de confirmation (avec lien de modification) comme pour une inscription en ligne. */
  prevenir: z.boolean().default(true),
});
